/**
 * Phase 6c PR 4 — adapters for `<TodayHero />` and `<SeasonGlance />`.
 *
 * Pure helpers that map the existing data shapes (TodaySession from
 * `useTodaySession` / `ramiroToday`; MacroPlanWeek[] from `useMacroPlan`;
 * Race from `useUpcomingRace`) to the props consumed by the new 6c
 * components.
 *
 * Pure-Node — no React, no Supabase. Unit-testable in isolation.
 */

import type { MacroPlanWeek } from '../hooks/useMacroPlan';
import { currentWeekFromDates, resolveCurrentWeek, daysUntilIso, formatWeekDate } from './macroPlanPhases';
import { resolveMode } from './trainingModes';
import type { ContextPill } from './dedupePills';
import type { ContextItem } from '../components/ContextDrawer/ContextDrawer';
import type { IconName } from '../components/icons/registry';
import type {
  HeroMetaItem,
  SeasonPhase,
} from '../components/TodayHero/TodayHero';
import type { Phase } from '../components/primitives/PhaseTrack';
import type { Day } from '../fixtures/plan';
import { sessionName } from './weekMeasure';

/**
 * Compose the hero h1 from a `TodaySession`'s split title fields.
 * The legacy split (`titleLead` + `titleEm` + `titleTrail`) was driven
 * by the dark hero's `<em>` highlight; the 6c hero just wants a plain
 * sentence.
 *
 * Examples:
 *   { lead: "Easy run,", em: "4 miles", trail: "." } → "Easy run, 4 miles."
 *   { lead: "", em: "Off day", trail: "" }            → "Off day"
 */
export function composeHeroTitle(parts: {
  lead: string;
  em: string;
  trail: string;
}): string {
  const em = parts.em.trim();
  // Reassemble preserving the original spacing convention. The fixture
  // uses comma-then-space-then-em ("Easy run, 4 miles."), so we keep
  // the original `lead` text intact (with its trailing punctuation +
  // space if any) and append em + trail.
  let out = '';
  out += parts.lead;
  if (out.length > 0 && !out.endsWith(' ') && em.length > 0) out += ' ';
  out += em;
  out += parts.trail;
  return out.trim() || em;
}

const KNOWN_PHASES: ReadonlySet<string> = new Set([
  'base',
  'build',
  'competition',
  'taper',
  'recovery',
]);

/**
 * Resolve a free-form phase label ("Today · Week 2 · Base" /
 * "Build week 7" / "Competition · Wk 5") to a `SeasonPhase` enum
 * by case-insensitive substring match. Returns null on no match
 * (caller hides the phase pill).
 */
export function phaseFromLabel(raw: string | undefined | null): SeasonPhase | null {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  for (const p of KNOWN_PHASES) {
    if (lower.includes(p)) return p as SeasonPhase;
  }
  return null;
}

/**
 * Resolve the current phase from a real macro plan, anchored on
 * `currentWeekFromDates`. Returns null when the macro plan is empty
 * or no week matches today.
 */
export function phaseFromMacroPlan(
  weeks: MacroPlanWeek[] | null,
  now: Date = new Date(),
): { phase: SeasonPhase | null; weekNumber: number | null } {
  if (!weeks || weeks.length === 0) return { phase: null, weekNumber: null };
  const currentWeek = currentWeekFromDates(weeks, now);
  if (currentWeek === null) return { phase: null, weekNumber: null };
  const row = weeks.find((w) => w.week_number === currentWeek);
  if (!row) return { phase: null, weekNumber: currentWeek };
  return {
    phase: KNOWN_PHASES.has(row.mode) ? (row.mode as SeasonPhase) : null,
    weekNumber: currentWeek,
  };
}

/**
 * F-509 — map a macro `SeasonPhase` to the `PhaseTrack` `Phase` used by the
 * Today right-rail race panel (ChampionshipCard). The two vocabularies differ:
 * the macro rows carry lowercase mode ids (`competition`), the 4-dot race
 * track carries capitalised `Phase` (`Peak`, displayed "Comp"). `recovery`
 * (post-race, no track cell) maps to the nearest late-arc cell, `Taper`.
 * Returns null for null input so the caller keeps the existing race.phase.
 */
const RACE_PHASE_BY_MODE: Record<SeasonPhase, Phase> = {
  base: 'Base',
  build: 'Build',
  competition: 'Peak',
  taper: 'Taper',
  recovery: 'Taper',
};

export function racePhaseFromMode(phase: SeasonPhase | null): Phase | null {
  return phase ? RACE_PHASE_BY_MODE[phase] : null;
}

/**
 * F-509 — the ACTUAL macro phase for the current week, mapped to the race
 * panel's `Phase`. Single source of truth so the panel can't show BUILD while
 * the season rail (which reads the same macro rows) shows BASE. Returns null
 * when no macro week matches today → caller keeps the race's own phase.
 */
export function racePhaseFromMacroPlan(
  weeks: MacroPlanWeek[] | null,
  now: Date = new Date(),
): Phase | null {
  return racePhaseFromMode(phaseFromMacroPlan(weeks, now).phase);
}

/**
 * Total weeks in a macro plan. Returns 0 for null/empty input so the
 * caller can short-circuit on `total === 0`.
 */
export function totalWeeksOf(weeks: MacroPlanWeek[] | null): number {
  return weeks?.length ?? 0;
}

/**
 * Phase short labels for the before_season hero's phase summary
 * ("Base → Build → Comp → Taper"). Mode taxonomy is locked; "competition"
 * displays as "Comp" (matches PhaseTrack's E4 display-label map).
 */
const PHASE_SHORT: Record<string, string> = {
  base: 'Base',
  build: 'Build',
  competition: 'Comp',
  taper: 'Taper',
  recovery: 'Rec',
};

/**
 * Ordered, de-duplicated phase summary across a macro plan, by first
 * appearance ("Base → Build → Comp → Taper"). Empty string for null/empty.
 */
export function phaseSummaryOf(weeks: MacroPlanWeek[] | null): string {
  if (!weeks || weeks.length === 0) return '';
  const sorted = [...weeks]
    .filter((w) => !!w.week_start_date)
    .sort((a, b) =>
      (a.week_start_date as string).localeCompare(b.week_start_date as string),
    );
  const seq: string[] = [];
  for (const w of sorted) {
    const label = PHASE_SHORT[w.mode];
    if (label && !seq.includes(label)) seq.push(label);
  }
  return seq.join(' → ');
}

/**
 * Props for the before_season `<TodayHero variant="beforeSeason">` (G1),
 * derived from the macro plan — but ONLY when the resolver says the season
 * hasn't started yet. Returns null otherwise (caller falls back to the
 * first-run hero), so a true no-plan / in_season / after_season state never
 * renders the countdown.
 *
 * `daysUntilStart` is the authoritative integer (today → first week, local
 * day) the hero's copy branches on (tomorrow / N days / N weeks). The hero is
 * only mounted while state === before_season, so it unmounts on the resolver's
 * before_season→in_season flip at the local Week-1 boundary.
 */
export function beforeSeasonHeroProps(
  weeks: MacroPlanWeek[] | null,
  seasonName: string | null,
  now: Date = new Date(),
): {
  seasonStartDate: Date;
  daysUntilStart: number;
  seasonName: string;
  weekCount: number;
  phaseSummary: string;
} | null {
  if (!weeks || weeks.length === 0) return null;
  if (resolveCurrentWeek(weeks, null, now).state !== 'before_season') return null;
  const dated = [...weeks]
    .filter((w) => !!w.week_start_date)
    .sort((a, b) =>
      (a.week_start_date as string).localeCompare(b.week_start_date as string),
    );
  if (dated.length === 0) return null;
  const startIso = dated[0].week_start_date as string;
  const [y, m, d] = startIso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return {
    seasonStartDate: new Date(y, m - 1, d),
    daysUntilStart: daysUntilIso(startIso, now),
    seasonName: seasonName && seasonName.trim() ? seasonName : 'Your season',
    weekCount: weeks.length,
    phaseSummary: phaseSummaryOf(weeks),
  };
}

/**
 * Season Phase 3 (P3-3) — Today lifecycle hero props for the EMPTY-session
 * branch (no loaded today session). Returns firstRun-shaped props (title /
 * body / cta) for two states the generic first-run hero used to mislabel:
 *
 *   - `in_season` but the current week ISN'T drafted yet → "Week N isn't
 *     drafted yet" + a "Draft week N" deep-link (the P3-2 chat flow). This is
 *     the dominant fresh-season state (a 16-week macro exists, 0 drafted) that
 *     used to read the brand-new "Let's build your first week."
 *   - `after_season` → the "Season complete — see your recap" affordance
 *     (lands on the /plan recap + roll-forward surface, SR-2).
 *
 * Returns null for before_season (handled by beforeSeasonHeroProps), no_plan /
 * invalid (→ the true first-run hero), and the rare in_season case where the
 * current week IS known-drafted but today has no entry (→ first-run fallback,
 * pre-existing behavior; never falsely claims "isn't drafted").
 *
 * `draftedWeeks` is the loaded set (or undefined while loading). Reaching the
 * empty-session branch while in_season already implies the current week is
 * undrafted (useTodaySession returns null with no plan_day for today), so an
 * undefined (still-loading) set does NOT suppress the hero — it only adds the
 * "drafted N so far" note and suppresses the rare known-drafted edge.
 */
export interface LifecycleHeroProps {
  weekNumber: number | null;
  /** SP-3 kicker (context line) — "Week 4 · Build · Week of Jul 27". Uppercased
   *  in CSS. Null for the season-complete variant (no current week). */
  kicker: string | null;
  /** Serif numeral for the 44px tile glyph. Null → tile renders empty
   *  (season-complete has no week number). */
  glyphNumeral: string | null;
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  /** Quiet secondary ("See the season plan ›"). Null → primary only. */
  secondaryLabel: string | null;
  secondaryHref: string | null;
}

export function lifecycleHeroProps(
  weeks: MacroPlanWeek[] | null,
  draftedWeeks: Set<number> | undefined,
  now: Date = new Date(),
  /** B-3 — when `draft_week_journey_v1` is ON, the "Week N isn't drafted" hero
   *  re-aims its body + CTA at the /v2/plan/draft takeover (was the chat
   *  deep-link). Default false = BYTE-IDENTICAL current hero (flag OFF path). */
  draftJourney = false,
): LifecycleHeroProps | null {
  if (!weeks || weeks.length === 0) return null;
  const res = resolveCurrentWeek(weeks, null, now);

  if (res.state === 'after_season') {
    // SP-3 C3 — season-complete keeps its existing copy, restyled to the
    // empty-state anatomy (no kicker / no tile numeral / no secondary).
    return {
      weekNumber: null,
      kicker: null,
      glyphNumeral: null,
      title: 'Season complete.',
      body: `Your ${weeks.length}-week season has wrapped. See your recap and start your next block from the season plan.`,
      ctaLabel: 'See season recap',
      ctaHref: '/v2/plan',
      secondaryLabel: null,
      secondaryHref: null,
    };
  }

  if (res.state === 'in_season' && res.weekNumber != null) {
    const wk = res.weekNumber;
    // Known-drafted current week (rare: drafted but today has no plan_day) →
    // don't claim "isn't drafted"; let the generic fallback render.
    if (draftedWeeks && draftedWeeks.has(wk)) return null;
    const row = weeks.find((w) => w.week_number === wk) ?? null;
    const modeLabel = resolveMode(row?.mode).label;
    const dateLabel = formatWeekDate(row?.week_start_date ?? null);
    // SP-3 C3 — phase + date move into the kicker; the body is now the fixed
    // ratified line ("The phase plan reserves it — …").
    const kicker = dateLabel
      ? `Week ${wk} · ${modeLabel} · Week of ${dateLabel}`
      : `Week ${wk} · ${modeLabel}`;
    return {
      weekNumber: wk,
      kicker,
      glyphNumeral: String(wk),
      title: `Week ${wk} isn't drafted yet.`,
      // B-3 re-aim: the phase plan reserves it — draft it, give it one look, and
      // it lands here → the /v2/plan/draft takeover. Flag OFF = the chat deep-link.
      body: draftJourney
        ? 'The phase plan reserves it. Draft it, give it one look, and it lands here.'
        : 'The phase plan reserves it. Draft the day-by-day with Airo and it lands here.',
      ctaLabel: `Draft week ${wk}`,
      ctaHref: draftJourney ? `/v2/plan/draft?weeks=${wk}&from=today` : `/v2/chat?draftWeek=${wk}`,
      secondaryLabel: 'See the season plan ›',
      secondaryHref: '/v2/plan',
    };
  }

  return null;
}

/**
 * Compose the hero meta row from common signal sources. Each item is
 * dropped if its value is empty, so callers can pass everything they
 * have and let the adapter prune.
 *
 * Order matches the design's reading rhythm:
 *   Pace · Gear · Weather · Route · Fuel
 *
 * (Direction.md §3.1 reads "Pace · Conversational  ·  Gear · Carbon
 * flats · break-in / Weather · 57° overcast · SE 6 mph · 6 pm".)
 */
export function composeHeroMeta(input: {
  pace?: string | null;
  gear?: string | null;
  weather?: string | null;
  route?: string | null;
  fuel?: string | null;
  /**
   * CD visual polish §1 — the ratified condition glyph for the Weather row,
   * resolved by `weatherGlyphFor()` from the same `condition` word the value
   * string already carries. Null / omitted (unknown or absent word) → the
   * Weather item is emitted with no glyph key at all, exactly as before, and
   * it can only ever ride a Weather row that is actually rendering.
   */
  weatherGlyph?: IconName | null;
}): HeroMetaItem[] {
  const out: HeroMetaItem[] = [];
  if (input.pace) out.push({ k: 'Pace', v: input.pace });
  if (input.gear) out.push({ k: 'Gear', v: input.gear });
  if (input.weather) {
    out.push({
      k: 'Weather',
      v: input.weather,
      ...(input.weatherGlyph ? { glyph: input.weatherGlyph } : {}),
    });
  }
  if (input.route) out.push({ k: 'Route', v: input.route });
  if (input.fuel) out.push({ k: 'Fuel', v: input.fuel });
  return out;
}

/**
 * Phase 6c long-tail PR — adapter for `<PinnedContext />` (Ultra
 * right-rail card). Maps the deduped athlete-context pills from
 * `useAthleteContext().pills` to the `ContextItem[]` shape the card
 * consumes.
 *
 * Pure mapping — pill.key → item.label, pill.value → item.value. The
 * `muted` flag is not derivable from the pill list (no priority
 * signal), so all items render at full opacity. Empty input maps to
 * empty output → PinnedContext returns null and the card hides.
 */
export function pillsToContextItems(pills: ContextPill[]): ContextItem[] {
  return pills.map((p) => ({ label: p.key, value: p.value }));
}

/* ── W1-TODAY-HERO: the 2.0 hero's pure derivations (`variant="v2"`) ──────────
 *
 * Everything the 2.0 hero shows beyond what `renderLoopHero` already hands the
 * hero is derived here, from the title it composed (`composeHeroTitle` over the
 * session, in the athlete's unit) and from the week's `Day`s. WIRE passes the
 * variant and nothing else (spec 7.4, SEAMS 1).
 */

/** The session name and the distance the existing title builder folded into the title. */
export interface HeroTitleParts {
  /** "Easy run": the session name alone, no trailing period, no distance (TH-3). */
  name: string;
  /**
   * The day's stated distance, exactly as `useTodaySession.buildTitle` folded it
   * into the title. That builder never states a rep or one leg of several
   * ("5x400m", "2 km warm-up, 6x1000m"), so what is here is never a partial total
   * and a rep-only day has none (TH-4, Q16).
   */
  distance: { value: string; unit: 'mi' | 'km' } | null;
}

const TITLE_DISTANCE_TAIL = /,\s*(\d+(?:\.\d+)?)\s*(miles?|km)$/i;
const TITLE_REPS_TAIL = /,\s*\d+\s*[x×]\s*\d+\s*m$/i;

/** Split a composed hero title ("Easy run, 7 km.", "Hill workout, 5x400m.", "Rest day.") into its parts. */
export function parseHeroTitle(title: string): HeroTitleParts {
  const text = title.trim().replace(/\.+$/, '').trim();
  const distance = TITLE_DISTANCE_TAIL.exec(text);
  if (distance) {
    return {
      name: text.slice(0, distance.index).trim(),
      distance: { value: distance[1], unit: distance[2].toLowerCase().startsWith('mi') ? 'mi' : 'km' },
    };
  }
  const reps = TITLE_REPS_TAIL.exec(text);
  if (reps) return { name: text.slice(0, reps.index).trim(), distance: null };
  return { name: text, distance: null };
}

/**
 * The hero stat line (TH-4): a stated distance in the athlete's unit ("7 km",
 * "4.3 mi"), else the day's minutes when it was authored in time ("40 min", a
 * range "35 to 40 min", Q16), else nothing. `dayMileage` is `Day.mileage`, which
 * `deriveDayMetric` writes as a duration only when the day states no distance
 * token at all. No "about N min" here: that waits for a pace range (D1).
 */
export function heroStatLine(
  parts: HeroTitleParts,
  dayMileage: string | null | undefined,
): string | null {
  if (parts.distance) return `${parts.distance.value} ${parts.distance.unit}`;
  const minutes = (dayMileage ?? '').trim();
  return /^\d+(?: to \d+)? min$/.test(minutes) ? minutes : null;
}

/**
 * "Easy run, 6 km": the session and its distance or minutes (string 28 pattern). Rest reads "Rest".
 * The session's name is the one every 2.0 surface prints, `weekMeasure.sessionName`
 * (W1-TODAY-FIX1, F4): this file used to keep a second table ("Hard workout" against
 * the week row's "Intervals" or "Hill workout"), so one day read two ways.
 */
export function sessionLineForDay(
  day: Pick<Day, 'label' | 'mileage' | 'mileageInUnit' | 'rest' | 'details'> & { workoutType?: string },
): string | null {
  // A weekday the plan does not have is unknown, not a rest day.
  if (day.label === '—') return null;
  if (day.rest) return 'Rest';
  const name = sessionName(day);
  const metric = (day.mileageInUnit ?? day.mileage ?? '').trim();
  const stated = metric && metric !== '—' && metric !== '?' ? metric : null;
  return stated ? `${name}, ${stated}` : name;
}

/**
 * Tomorrow's session for the rest-day hero (TH-10), or null when tomorrow is not
 * in the loaded week (a Sunday: only the current week is loaded, D3) or unknown.
 */
export function tomorrowSessionLine(days: Day[]): string | null {
  const at = days.findIndex((d) => d.today);
  if (at < 0) return null;
  const next = days[at + 1];
  return next ? sessionLineForDay(next) : null;
}
